/**
 * @fileoverview Ops console — runtime feature flags. Each flag resolves through
 * DB override → environment variable → registry default, and this page writes
 * only the DB layer: saving sets an override, "Revert to deploy value" clears it
 * (PUT with `value: null`) and hands control back to the environment.
 *
 * Two deliberate frictions, because these switches move production behaviour for
 * every account at once:
 *   1. A flag carrying a `caution` string opens a confirmation dialog quoting it
 *      before anything is sent.
 *   2. The deploy-time `env_value` is always shown next to the live value, so an
 *      operator can see what reverting would restore before they revert.
 *
 * Bool toggles update optimistically and roll back on failure (the OrgFeatures
 * pattern); string flags are explicit save-on-submit, since a half-typed org id
 * should never be dispatched.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import opsApi, { getOpsErrorMessage } from "@/lib/opsApi";
import { groupFlags, isFlagOn, displayValue, sourceLabel } from "@/lib/opsFlags";
import { Card, SectionHeader, Button, Tag, Icon } from "@/components/studio/primitives";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const field =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

function Toggle({ on, onClick, testId, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-pressed={on}
      className={`relative shrink-0 rounded-full transition-colors disabled:opacity-50 ${
        on ? "bg-[var(--accent-yellow)]" : "bg-[var(--studio-interactive)]"
      }`}
      style={{ width: 40, height: 22 }}
    >
      <span
        className="absolute top-0.5 rounded-full bg-[var(--studio-knob)] shadow-sm transition-transform"
        style={{ width: 18, height: 18, left: 2, transform: on ? "translateX(18px)" : "translateX(0)" }}
      />
    </button>
  );
}

/** Where the live value came from + what a revert would restore. */
function SourceLine({ flag }) {
  const overridden = flag.source === "db";
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {/* Tag takes no extra props, so the test hook rides a wrapping span. */}
      <span data-testid={`ops-flag-source-${flag.key}`}>
        {overridden ? (
          <Tag accent>
            <Icon name="alert" size={12} strokeWidth={2} />
            Overridden
          </Tag>
        ) : (
          <Tag>{sourceLabel(flag.source)}</Tag>
        )}
      </span>
      <span className="font-mono text-[12px] text-[var(--text-tertiary)]">
        deploy value: {displayValue(flag.env_value)}
      </span>
    </div>
  );
}

export default function OpsFlagsPage() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // key currently in flight
  const [drafts, setDrafts] = useState({}); // key -> in-progress string value
  const [pending, setPending] = useState(null); // { key, value, label, caution, verb }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await opsApi.get("/ops/flags");
      setFlags(resp.data.flags || []);
    } catch (err) {
      toast.error(getOpsErrorMessage(err, "Couldn't load flags"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Send one flag write. `optimistic` (bool toggles only) paints the new state
   * immediately and is rolled back if the PUT fails; the server's returned
   * `flags` array is always the final word.
   */
  const commit = useCallback(
    async ({ key, value }, { optimistic = false } = {}) => {
      const prev = flags;
      if (optimistic) {
        setFlags((current) =>
          current.map((f) => (f.key === key ? { ...f, value, source: value === null ? "env" : "db" } : f))
        );
      }
      setSaving(key);
      try {
        const resp = await opsApi.put("/ops/flags", { key, value });
        setFlags(resp.data.flags || prev);
        setDrafts((d) => {
          const next = { ...d };
          delete next[key];
          return next;
        });
        toast.success(value === null ? `${key} reverted to the deploy value` : `${key} updated`);
      } catch (err) {
        if (optimistic) setFlags(prev); // roll back
        toast.error(getOpsErrorMessage(err, "Couldn't save"));
      } finally {
        setSaving(null);
      }
    },
    [flags]
  );

  /**
   * Route a change through the caution dialog when the flag declares one; apply
   * it straight away otherwise.
   */
  const request = (flag, value, verb) => {
    const change = { key: flag.key, value, optimistic: flag.kind === "bool" };
    if (flag.caution) {
      setPending({ ...change, label: flag.label, caution: flag.caution, verb });
      return;
    }
    commit(change, { optimistic: change.optimistic });
  };

  const confirmPending = () => {
    const change = pending;
    setPending(null);
    if (change) commit(change, { optimistic: change.optimistic });
  };

  const groups = groupFlags(flags);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
      <h1 className="mb-2 font-serif text-2xl font-semibold text-[var(--text-primary)]">Flags</h1>
      <p className="mb-6 font-sans text-[13px] text-[var(--text-tertiary)]">
        Runtime switches, live across the fleet within about a minute of saving. A flag with no override here follows
        whatever the last deploy set.
      </p>

      {loading ? (
        <Card>
          <div className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <div className="font-sans text-[15px] text-[var(--text-tertiary)]">No runtime-editable flags.</div>
        </Card>
      ) : (
        groups.map(({ group, flags: groupFlagList }) => (
          <Card key={group} className="mb-6" data-testid={`ops-flag-group-${group}`}>
            <SectionHeader>{group}</SectionHeader>
            <div className="flex flex-col gap-6">
              {groupFlagList.map((flag) => {
                const busy = saving === flag.key;
                const on = isFlagOn(flag.value);
                const draft = drafts[flag.key] ?? (flag.value ?? "");
                const dirty = draft !== (flag.value ?? "");
                return (
                  <div key={flag.key} className="border-t border-[var(--studio-divider)] pt-5 first:border-t-0 first:pt-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-sans text-[15px] text-[var(--text-primary)]">{flag.label}</div>
                        <div className="mt-0.5 font-mono text-[12px] text-[var(--text-quaternary)]">{flag.key}</div>
                        <div className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]">{flag.description}</div>
                      </div>
                      {flag.kind === "bool" && (
                        <Toggle
                          on={on}
                          disabled={busy}
                          testId={`ops-flag-${flag.key}`}
                          onClick={() => request(flag, on ? "false" : "true", on ? "Turn off" : "Turn on")}
                        />
                      )}
                    </div>

                    {flag.kind !== "bool" && (
                      <form
                        className="mt-3 flex flex-col gap-2 sm:flex-row"
                        onSubmit={(e) => {
                          e.preventDefault();
                          request(flag, draft.trim() === "" ? null : draft.trim(), "Save");
                        }}
                      >
                        {flag.choices?.length ? (
                          <select
                            value={draft}
                            onChange={(e) => setDrafts((d) => ({ ...d, [flag.key]: e.target.value }))}
                            data-testid={`ops-flag-input-${flag.key}`}
                            className={field + " flex-1"}
                          >
                            <option value="">Not set</option>
                            {flag.choices.map((choice) => (
                              <option key={choice} value={choice}>
                                {choice}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={draft}
                            onChange={(e) => setDrafts((d) => ({ ...d, [flag.key]: e.target.value }))}
                            placeholder="Not set"
                            data-testid={`ops-flag-input-${flag.key}`}
                            className={field + " flex-1"}
                          />
                        )}
                        <Button
                          type="submit"
                          variant="ghost"
                          disabled={busy || !dirty}
                          data-testid={`ops-flag-save-${flag.key}`}
                        >
                          {busy ? "Saving…" : "Save"}
                        </Button>
                      </form>
                    )}

                    <SourceLine flag={flag} />

                    {flag.source === "db" && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => request(flag, null, "Revert")}
                        data-testid={`ops-flag-revert-${flag.key}`}
                        className="mt-2 font-sans text-[13px] font-medium text-[var(--text-secondary)] hover:underline disabled:opacity-50"
                      >
                        Revert to deploy value
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))
      )}

      {/* Caution confirmation — the flag's own warning text, verbatim. */}
      <Dialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent className="studio max-w-md border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)]">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg font-semibold text-[var(--text-primary)]">
              {pending?.verb} {pending?.label}?
            </DialogTitle>
            <DialogDescription className="font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
              {pending?.caution}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="bare" onPress={() => setPending(null)} data-testid="ops-caution-cancel">
              Cancel
            </Button>
            <Button onPress={confirmPending} data-testid="ops-caution-confirm">
              {pending?.verb === "Revert" ? "Revert" : "Apply"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
